<template>
    <div v-for="option in availableOptions" :key="option.id" class="mb-4">
        <h5 class="mb-2 font-medium">{{ option.name }}</h5>

        <ColorProductOptions v-if="option.display == 'colors'" :options="getConstrainedOptionValues(option)" :selected="selectedOptions[option.id]"  @selectOption="(valueId : number) => selectOption(option.id, valueId)" />
        <DropdownProductOptions v-else-if="option.values.length > 15 || option.display == 'dropdown'" :options="getConstrainedOptionValues(option)" :selected="selectedOptions[option.id]" @selectOption="(valueId : number) => selectOption(option.id, valueId)" />
        <ButtonProductOptions v-else :option="option"  :options="getConstrainedOptionValues(option)" :selected="selectedOptions[option.id]" @selectOption="(valueId : number) => selectOption(option.id, valueId)" />
     </div>
</template>
<script setup lang="ts">
import type { ProductOption, ProductOptionValue, ProductVariant } from '~/types';
import ButtonProductOptions from './ButtonProductOptions.vue';
import DropdownProductOptions from './DropdownProductOptions.vue';
import ColorProductOptions from './ColorProductOptions.vue';

const {
    variants,
    options,
    autoselect = false,
    variant
} = defineProps<{
  options: ProductOption[],
  variants: ProductVariant[],
  variant: ProductVariant | null,
  autoselect?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:variant', value: ProductVariant | null): void
}>()


const selectedOptions = ref<{ [key: number]: number }>({})
const constraintKey = ref<number | null>(null) // last clicked option is prioritized

const availableOptions = computed(() => {
    return options.filter((option : ProductOption) => {
        return variants.some(variant => variant.options.hasOwnProperty(option.id))
    }).map((option : ProductOption) => {
        option.values = option.values.filter((value : any) => {
            return variants.some(variant => Object.values(variant.options).includes(value.id))
        })
        return option
    })
})

const constraintVariants = computed(() => {
    const key = constraintKey.value

    if (key === null || typeof selectedOptions.value[key] === 'undefined') {
        return variants
    }

    return getConstraintVariants(key, selectedOptions.value[key])
})

onMounted(() => {
  if ((autoselect || variant) && variants.length > 0) {
    const autoVariant = variant || variants[0]
    selectedOptions.value = { ...autoVariant!.options }
    // assume first option was selected
    constraintKey.value = availableOptions.value[0]?.id || null
  }
})

watchEffect(() => {
    // Find matching variant given selected options
    const variant = variants.find(v => {
        return Object.entries(v.options).every(([optionId, valueId]) => {
            return selectedOptions.value[Number(optionId)] === valueId
        })
    })

    if (variant) {
        emit('update:variant', variant)
    } else {
        emit('update:variant', null)
    }
})

const selectOption = (optionId: number, valueId: number) => {
    if (selectedOptions.value[optionId] === valueId) {
        delete selectedOptions.value[optionId]
        constraintKey.value = Object.keys(selectedOptions.value).length > 0 ? Number(Object.keys(selectedOptions.value)[0]) : null
    } else {
        const constraintVariants = getConstraintVariants(optionId, valueId)

        // Remove conflicting options
        Object.entries(selectedOptions.value).filter(([selectedOptionId, selectedValueId]) => {
            return !constraintVariants.some(variant => variant.options[Number(selectedOptionId)] === selectedValueId)
        }).forEach(([selectedOptionId, _]) => {
            delete selectedOptions.value[Number(selectedOptionId)]
        })

        selectedOptions.value[optionId] = valueId
        constraintKey.value = optionId
    }
}

const getConstraintVariants = (key: number, value: number) : ProductVariant[] => {
    return variants.filter(variant => variant.options[key] === value)
}

const getConstrainedOptionValues = (option : ProductOption) : (ProductOptionValue & { available: boolean })[] => {
    return option.values.map((value : ProductOptionValue) => {
        return {
            ...value,
            available: constraintKey.value === null || constraintKey.value === option.id ? true : constraintVariants.value.some(v => v.options[option.id] === value.id)
        }
    })
}
</script>